import React, { useEffect, useState, Fragment } from "react";
import { useTranslation } from 'react-i18next';
import { useNavigate } from "react-router-dom";
import { Row, Col, Card, CardBody } from "reactstrap";
import { Tooltip } from "@mui/material";
import LoadingOverlay from "react-loading-overlay-ts";
import DNALoader from "../../components/Loaders/DnaLoader";
import BusinessService from "./Businesses.service.js";
import AuthService from "../Authentication/auth.service.js";
import Alerts from "../../components/Alerts/alerts.js";
import ConfigurationService from "../Configuration/configuration.service.js";

const COMMISSION_PLACEHOLDER = "If no commission rate is set, default will be used";

const TabFinancialDoctor = ({ business_id }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [editingFinancial, setEditingFinancial] = useState(false);
  const [shareInput, setShareInput] = useState("");
  const [rate, setRate] = useState("");
  const [saraAppointmentShare, setSaraAppointmentShare] = useState(null);
  const [defaultShare, setDefaultShare] = useState(null);

  const fetchDefaultShare = async () => {
    try {
      const sessionID = localStorage.getItem("session-id");
      const response = await ConfigurationService.getConfiguration(sessionID);
      if (response.data?.data?.[0]?.sara_share != null) {
        setDefaultShare(response.data.data[0].sara_share);
      }
    } catch (error) {
      console.error("Failed to load default commission rate:", error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    BusinessService.GetSingleDoctor(business_id).then((response) => {
      setLoading(false);
      if (response.data.message === "Unauthorized") {
        AuthService.logout();
        navigate("/login");
        window.location.reload();
      } else if (response.data.status === true) {
        const doctor = response.data.data.data;
        setRate(doctor.rate);
        setSaraAppointmentShare(doctor.sara_appointment_share);
        const share =
          doctor.sara_appointment_share != null && doctor.sara_appointment_share !== ''
            ? String(doctor.sara_appointment_share)
            : '';
        setShareInput(share);
      } else {
        Alerts.swalErrorAlert(t("Something went wrong, Please try again."));
      }
    }, (error) => {
      setLoading(false);
      const errContent = (error.response && error.response.data) || error.message || error.toString();
      Alerts.swalErrorAlert(errContent || t("Server Error, Please try again later."));
    });
  };

  useEffect(() => {
    fetchDefaultShare();
    fetchData();
  }, [business_id]);

  const handleSaveFinancial = async () => {
    if (shareInput !== '' && (parseFloat(shareInput) < 0 || parseFloat(shareInput) > 100)) {
      Alerts.swalErrorAlert(t('Commission rate must be between 0 and 100'));
      return;
    }
    setLoading(true);
    try {
      const response = await BusinessService.UpdateDoctorSaraShare(
        business_id,
        shareInput === '' ? '' : parseFloat(shareInput)
      );
      setLoading(false);
      if (response.data.status) {
        setEditingFinancial(false);
        await fetchData();
        Alerts.swalSuccessAlert(t('Financial information updated successfully'));
      } else {
        Alerts.swalErrorAlert(t('Something went wrong, Please try again.'));
      }
    } catch (error) {
      setLoading(false);
      Alerts.swalErrorAlert(t('Server Error, Please try again later.'));
    }
  };

  const shareDisplay =
    saraAppointmentShare != null && saraAppointmentShare !== ''
      ? `${saraAppointmentShare}%`
      : defaultShare != null
        ? `${t('Default')} (${defaultShare}%)`
        : t('Default');

  return (
    <Fragment>
      <LoadingOverlay tag="div" active={loading} spinner={<DNALoader />}>
        <Row>
          <Col md="6">
            <Card className="h-100">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="card-title mb-0 fw-bold">
                    <i className="pe-7s-credit me-2 text-primary"></i>
                    {t('Financial Information')}
                  </h5>
                  <Tooltip placement="top" title={editingFinancial ? t('Cancel Edit') : t('Edit Financial Info')} arrow>
                    <button
                      type="button"
                      onClick={() => {
                        if (editingFinancial) {
                          const share =
                            saraAppointmentShare != null && saraAppointmentShare !== ''
                              ? String(saraAppointmentShare)
                              : '';
                          setShareInput(share);
                        }
                        setEditingFinancial(!editingFinancial);
                      }}
                      className={`btn btn-sm ${editingFinancial ? 'btn-secondary' : 'btn-custom-color'} btn-hover-shine`}
                    >
                      <i className={`pe-7s-${editingFinancial ? 'close' : 'edit'}`}></i>{' '}
                      {editingFinancial ? t('Cancel') : t('Edit')}
                    </button>
                  </Tooltip>
                </div>

                {editingFinancial ? (
                  <div>
                    <Row>
                      <Col md="12" className="mb-3">
                        <label className="form-label fw-bold">{t('Commission Rate')} (%)</label>
                        <input
                          type="number"
                          name="sara_appointment_share"
                          value={shareInput}
                          onChange={(e) => setShareInput(e.target.value)}
                          step="0.01"
                          min="0"
                          max="100"
                          className="form-control form-control-sm"
                          placeholder={t(COMMISSION_PLACEHOLDER)}
                        />
                      </Col>
                    </Row>
                    <div className="d-flex justify-content-end mt-3">
                      <button
                        type="button"
                        onClick={handleSaveFinancial}
                        className="btn btn-success btn-hover-shine"
                      >
                        <i className="pe-7s-check"></i> {t('Save Changes')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <Row>
                    <Col md="12" className="mb-3">
                      <div className="d-flex flex-column">
                        <span className="text-muted small fw-bold">{t('Commission Rate')}</span>
                        <span className="text-dark">{shareDisplay}</span>
                      </div>
                    </Col>
                    <Col md="12" className="mb-3">
                      <div className="d-flex flex-column">
                        <span className="text-muted small fw-bold">{t('Consultancy Charges')}</span>
                        <span className="text-dark">RM {rate || t('Not specified')}</span>
                      </div>
                    </Col>
                  </Row>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </LoadingOverlay>
    </Fragment>
  );
};

export default TabFinancialDoctor;
